/**
 * FixtureSeedCard — one-click seed/repair of the canonical fixture cast
 * (Austin Electric Co./Mike Johnson + Hill Country Plumbing/Sarah Martinez)
 * on fixture project 7a0a0894…. Idempotent: re-runs update in place,
 * never duplicate. Typed confirm "SEED FIXTURE" required.
 *
 * Backend: POST /api/admin/fixture-seed  {confirm: "SEED FIXTURE"}
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CONFIRM_WORD = 'SEED FIXTURE';

export default function FixtureSeedCard({ isSuperAdmin }) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!isSuperAdmin) return null;
  const canRun = confirmText === CONFIRM_WORD && !busy;

  const onRun = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const tok = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const r = await fetch(`${API}/api/admin/fixture-seed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ confirm: CONFIRM_WORD }),
      });
      const body = await r.json().catch(() => null);
      if (!r.ok) setError(body?.detail || `HTTP ${r.status}`);
      else { setResult(body); setConfirmText(''); }
    } catch (e) { setError(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div id="fixture-seed" className="p-5 rounded-xl border border-blue-200 bg-blue-50/40 mb-6" data-testid="fixture-seed-card">
      <div className="flex items-start gap-3 mb-3">
        <CheckCircle2 className="w-5 h-5 text-blue-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-blue-950">Fixture Seed · canonical test cast</h3>
          <p className="text-sm text-blue-900 mt-1">
            One-click <strong>create-or-repair</strong> for Mike Johnson (Austin Electric Co.)
            + Sarah Martinez (Hill Country Plumbing) on the fixture project. Each gets a
            contractor row, linked contact, and contractor-type project membership. Idempotent —
            running twice fixes drift, never duplicates. Repairs the legacy
            <code className="mx-1 bg-white px-1 rounded">.co</code> email typo.
          </p>
        </div>
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="text-xs font-medium text-blue-900 block mb-1">
            Type <code className="bg-white px-1 rounded">{CONFIRM_WORD}</code> to confirm
          </label>
          <Input
            data-testid="fixture-seed-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={CONFIRM_WORD}
            disabled={busy}
          />
        </div>
        <Button
          data-testid="fixture-seed-run-btn"
          onClick={onRun}
          disabled={!canRun}
          className="bg-blue-700 hover:bg-blue-800 text-white"
        >
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Seeding…</> : 'Seed fixture'}
        </Button>
      </div>

      {error && (
        <div className="mt-3 p-3 rounded border border-rose-300 bg-rose-50 text-rose-900 text-sm" data-testid="fixture-seed-error">
          <AlertTriangle className="w-4 h-4 inline mr-1" />{error}
        </div>
      )}

      {result && (
        <div className="mt-3 p-3 rounded border border-emerald-300 bg-white text-sm" data-testid="fixture-seed-result">
          <div className="font-medium text-emerald-900 mb-2">
            <CheckCircle2 className="w-4 h-4 inline mr-1" />
            Seeded {result.cast_size} member(s) on project {(result.project_id || '').slice(0, 8)}…
          </div>
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left border-b">
                <th className="pb-1">Company / Contact</th>
                <th>Email</th>
                <th>Contractor</th>
                <th>Contact</th>
                <th>Membership</th>
              </tr>
            </thead>
            <tbody>
              {(result.per_member || []).map((m, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`fixture-seed-row-${i}`}>
                  <td className="py-1">{m.cast?.company_name} · {m.cast?.contact_name}</td>
                  <td>
                    {m.cast?.email}
                    {m.email_repaired_from && (
                      <div className="text-[10px] text-amber-700">repaired from {m.email_repaired_from}</div>
                    )}
                  </td>
                  <td><span className="px-1 bg-slate-100 rounded">{m.contractor_action}</span></td>
                  <td><span className="px-1 bg-slate-100 rounded">{m.contact_action}</span></td>
                  <td><span className="px-1 bg-slate-100 rounded">{m.membership_action}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
